import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FloatingActionButton, Icon } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RECORDING_CONTENT, RECORDING_VIDEOS, SOCIAL_LINKS } from '../../constants';
import { fetchYouTubeTitle, getYouTubeVideoId } from '../../utils/helpers';

/**
 * Get YouTube thumbnail with fallback qualities
 * Tries maxresdefault first, falls back to hqdefault, then 0.jpg
 */
const getReliableThumbnail = (videoId) => {
  if (!videoId) return '';
  // Use 0.jpg which is always available for all YouTube videos
  return `https://img.youtube.com/vi/${videoId}/0.jpg`;
};

/**
 * Recordings Page - Showcases Matisa's studio recordings & playback singing
 * Features a unique vinyl/studio aesthetic while matching the overall theme
 */
const Recordings = () => {
  const navigate = useNavigate();
  const [videoTitles, setVideoTitles] = useState({});
  const [activeVideoIndex, setActiveVideoIndex] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Page-specific meta information
  const pageTitle = "Studio Recordings | Matisa - Playback Singer & Recording Artist";
  const pageDescription = "Explore Matisa's studio recordings and playback singing work - professional vocals for films, ads, jingles, and music productions.";

  // Fetch titles for recording videos on mount
  useEffect(() => {
    const fetchTitles = async () => {
      const titles = {};
      await Promise.all(
        RECORDING_VIDEOS.map(async (video) => {
          titles[video.id] = await fetchYouTubeTitle(video.url);
        })
      );
      setVideoTitles(titles);
    };
    fetchTitles();
  }, []);

  // Process videos with titles
  const processedVideos = useMemo(() => {
    return RECORDING_VIDEOS.map((video) => ({
      ...video,
      title: videoTitles[video.id] || 'Loading...',
      thumbnail: getReliableThumbnail(getYouTubeVideoId(video.url)),
    }));
  }, [videoTitles]);

  // Handle video selection
  const handleVideoSelect = (index) => {
    setActiveVideoIndex(index);
    setIsVideoPlaying(true);
  };

  // Handle closing video player
  const handleClosePlayer = () => {
    setIsVideoPlaying(false);
    setActiveVideoIndex(null);
  };

  // Get embed URL for active video
  const getEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
  };

  return (
    <div className="w-full max-w-[480px] min-h-screen-safe bg-background-dark shadow-2xl overflow-hidden flex flex-col relative mx-auto">
      {/* Page Meta Tags - React 19 hoists these to <head> */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://matisa.in/recordings" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content="https://matisa.in/hero_main.png" />
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content="https://matisa.in/hero_main.png" />
      
      {/* Sticky Header */}
      <PageHeader title="Matisa" />

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Hero Section with Vinyl Aesthetic */}
        <section className="relative overflow-hidden">
          {/* Animated Background Gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-neon-pink/20 via-background-dark to-primary/20" />
          
          {/* Floating Music Notes Animation */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-10 left-5 text-neon-pink/20 animate-pulse">
              <Icon name="music_note" size="2xl" />
            </div>
            <div className="absolute top-20 right-8 text-primary/20 animate-pulse delay-300">
              <Icon name="music_note" size="xl" />
            </div>
            <div className="absolute bottom-10 left-1/4 text-gold/20 animate-pulse delay-500">
              <Icon name="music_note" size="lg" />
            </div>
          </div>

          {/* Vinyl Record Visual */}
          <div className="relative flex flex-col items-center pt-8 pb-6 px-6">
            {/* Rotating Vinyl */}
            <div className="relative mb-6">
              {/* Outer Ring Glow */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-neon-pink via-primary to-gold blur-xl opacity-40 animate-spin-slow" style={{ animationDuration: '8s' }} />
              
              {/* Vinyl Record */}
              <div className="relative w-40 h-40 rounded-full bg-gradient-to-br from-gray-900 via-gray-800 to-black shadow-2xl animate-spin-slow" style={{ animationDuration: '6s' }}>
                {/* Grooves */}
                <div className="absolute inset-2 rounded-full border border-white/5" />
                <div className="absolute inset-4 rounded-full border border-white/5" />
                <div className="absolute inset-6 rounded-full border border-white/5" />
                <div className="absolute inset-8 rounded-full border border-white/5" />
                <div className="absolute inset-10 rounded-full border border-white/5" />
                
                {/* Center Label */}
                <div className="absolute inset-12 rounded-full bg-gradient-to-br from-neon-pink to-primary flex items-center justify-center">
                  <span className="text-white text-[8px] font-bold tracking-widest">MATISA</span>
                </div>
                
                {/* Center Hole */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-background-dark" />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-white text-3xl font-bold tracking-tight text-center">
              {RECORDING_CONTENT.title}
            </h1>
            <p className="text-neon-pink text-sm font-medium tracking-wider uppercase mt-1">
              {RECORDING_CONTENT.subtitle}
            </p>
            
            {/* Tagline */}
            <p className="text-gold text-sm font-medium italic text-center mt-3 px-4">
              "{RECORDING_CONTENT.tagline}"
            </p>
          </div>
        </section>

        {/* Description Section */}
        <section className="px-6 pb-6">
          <div className="relative p-5 rounded-2xl bg-gradient-to-br from-white/5 to-transparent border border-white/10">
            {/* Mic Icon */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 p-2 rounded-full bg-background-dark border border-neon-pink/30">
              <Icon name="mic" size="md" className="text-neon-pink" />
            </div>
            
            <p className="text-[#90a9cb] text-sm leading-relaxed text-center pt-2">
              {RECORDING_CONTENT.description}
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="px-6 pb-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="headphones" size="sm" className="text-primary" />
            <h2 className="text-white text-lg font-bold tracking-tight">What I Offer</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {RECORDING_CONTENT.services.map((service, index) => (
              <div
                key={index}
                className="group relative p-4 rounded-2xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 hover:border-neon-pink/30 transition-all cursor-default overflow-hidden"
              >
                {/* Hover Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-neon-pink/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div className="relative">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-neon-pink/20 to-primary/20 flex items-center justify-center mb-3">
                    <Icon name={service.icon} size="md" className="text-neon-pink" />
                  </div>
                  <h3 className="text-white text-sm font-semibold mb-1">{service.title}</h3>
                  <p className="text-[#90a9cb]/70 text-[11px] leading-relaxed">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="mx-6 h-px bg-gradient-to-r from-transparent via-neon-pink/30 to-transparent" />

        {/* Featured Recordings Section */}
        <section className="p-6 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="album" size="sm" className="text-gold" />
              <h2 className="text-white text-lg font-bold tracking-tight">Featured Recordings</h2>
            </div>
            <span className="text-[#90a9cb] text-xs">{processedVideos.length} tracks</span>
          </div>

          {/* Track List - Premium Music Player Style */}
          <div className="flex flex-col gap-3">
            {processedVideos.map((video, index) => (
              <button
                key={video.id}
                onClick={() => handleVideoSelect(index)}
                className="group relative flex items-center gap-4 p-3 rounded-2xl bg-gradient-to-r from-white/5 to-transparent border border-white/10 hover:border-neon-pink/30 hover:bg-white/5 transition-all text-left"
              >
                {/* Thumbnail */}
                <div className="relative w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-neon-pink/20 to-primary/20">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Hide broken image and show fallback gradient with icon
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Fallback Icon (visible when image fails) */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Icon name="music_note" size="lg" className="text-neon-pink/60" />
                  </div>
                  {/* Play Overlay */}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-8 h-8 rounded-full bg-neon-pink/90 flex items-center justify-center">
                      <Icon name="play_arrow" size="sm" className="text-white ml-0.5" filled />
                    </div>
                  </div>
                </div>

                {/* Track Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white text-sm font-semibold truncate group-hover:text-neon-pink transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-neon-pink/80 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-neon-pink/10 rounded-full">
                      Recording
                    </span>
                    <span className="text-[#90a9cb]/50 text-[10px]">
                      Matisa Records
                    </span>
                  </div>
                </div>

                {/* Play Icon */}
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-neon-pink/20 transition-colors">
                  <Icon name="play_arrow" size="md" className="text-white/70 group-hover:text-neon-pink ml-0.5" />
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Waveform Decoration */}
        <section className="px-6 pb-6">
          <div className="relative h-16 rounded-2xl bg-gradient-to-r from-neon-pink/10 via-primary/10 to-gold/10 border border-white/5 overflow-hidden">
            {/* Animated Waveform Bars */}
            <div className="absolute inset-0 flex items-center justify-center gap-1 px-4">
              {[...Array(32)].map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-gradient-to-t from-neon-pink/60 to-primary/60 rounded-full animate-pulse"
                  style={{
                    height: `${Math.random() * 60 + 20}%`,
                    animationDelay: `${i * 0.05}s`,
                    animationDuration: `${0.5 + Math.random() * 0.5}s`,
                  }}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-6 pb-6">
          <div className="relative p-6 rounded-2xl bg-gradient-to-br from-neon-pink/15 via-background-dark to-primary/15 border border-neon-pink/20 overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-2 right-2 w-20 h-20 rounded-full border border-white/20" />
              <div className="absolute bottom-2 left-2 w-16 h-16 rounded-full border border-white/20" />
            </div>
            
            <div className="relative text-center">
              <h3 className="text-white text-lg font-bold mb-2">
                Need a Voice for Your Project?
              </h3>
              <p className="text-[#90a9cb] text-sm mb-4">
                From jingles to feature films, let's create something beautiful together.
              </p>
              <button
                onClick={() => {
                  const scrollContainer = document.querySelector('.overflow-y-auto');
                  if (scrollContainer) scrollContainer.scrollTo(0, 0);
                  navigate('/enquiry?type=recording');
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-neon-pink to-primary text-white font-semibold rounded-full hover:opacity-90 transition-opacity"
              >
                <Icon name="mail" size="sm" />
                {RECORDING_CONTENT.cta.primary}
              </button>
            </div>
          </div>
        </section>

        {/* YouTube Link */}
        <section className="px-6 pb-24">
          <div className="flex justify-center">
            <a 
              href={SOCIAL_LINKS.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 text-[#90a9cb] transition-colors hover:text-neon-pink"
            >
              <span className="text-sm font-semibold">Listen on YouTube</span>
              <Icon name="open_in_new" size="sm" className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </section>
      </main>

      {/* Video Player Modal */}
      {isVideoPlaying && activeVideoIndex !== null && (
        <div className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center animate-fade-in">
          {/* Close Button */}
          <button
            onClick={handleClosePlayer}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Close video"
          >
            <Icon name="close" size="lg" />
          </button>

          {/* Video Container */}
          <div className="w-full max-w-[480px] px-4">
            <div className="relative aspect-video w-full rounded-2xl overflow-hidden bg-black">
              <iframe
                src={getEmbedUrl(processedVideos[activeVideoIndex].url)}
                title={processedVideos[activeVideoIndex].title}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            
            {/* Video Title */}
            <div className="mt-4 text-center">
              <h3 className="text-white text-lg font-semibold">
                {processedVideos[activeVideoIndex].title}
              </h3>
              <p className="text-neon-pink text-sm mt-1">Matisa Records</p>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Button */}
      <FloatingActionButton to="/enquiry" />

      {/* Bottom Blur Overlay */}
      <div className="pointer-events-none fixed bottom-0 left-0 h-12 w-full bg-gradient-to-t from-background-dark to-transparent lg:absolute" />

      {/* Custom Styles for Animations */}
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 6s linear infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .delay-300 { animation-delay: 0.3s; }
        .delay-500 { animation-delay: 0.5s; }
      `}</style>
    </div>
  );
};

export default Recordings;
